import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useHolidayService from '../../app/services/hooks/useHolidayService';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import InputField from '../../components/InputField';
import SelectTwo from '../../components/SelectTwo';

interface Holiday {
    id: number;
    name: string;
    description: string | null;
    date: string;
    date_formatted: string;
    recurrence_type: string;
    recurrence_type_display: string;
    recurrence_description: string;
    day_of_week: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

const recurrenceTypeOptions = [
    { value: '', label: 'Todos los tipos' },
    { value: 'unique', label: 'Único (una sola vez)' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'annual', label: 'Anual' }
];

const HolidayList: React.FC = () => {
    const history = useHistory();
    const { requestConfirmation } = useReactConfirmAlert();
    const {
        getHolidays,
        deleteHoliday,
        activeHoliday,
        fetchingGetHolidays,
        fetchingDeleteHoliday,
        fetchingActiveHoliday
    } = useHolidayService();
    
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [filteredHolidays, setFilteredHolidays] = useState<Holiday[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [recurrenceFilter, setRecurrenceFilter] = useState('');
    
    useEffect(() => {
        loadHolidays();
    }, []);
    
    useEffect(() => {
        filterData();
    }, [searchTerm, recurrenceFilter, holidays]);
    
    const loadHolidays = () => {
        getHolidays({}, {
            onSuccess: (res: any) => {
                setHolidays(res.data.holidays || []);
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar feriados');
            }
        });
    };
    
    const filterData = () => {
        let filtered = [...holidays];
        
        // Filtrar por búsqueda
        if (searchTerm) {
            filtered = filtered.filter(holiday =>
                holiday.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                holiday.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Filtrar por tipo de recurrencia
        if (recurrenceFilter) {
            filtered = filtered.filter(holiday =>
                holiday.recurrence_type === recurrenceFilter
            );
        }
        
        setFilteredHolidays(filtered);
    };
    
    const handleEdit = (id: number) => {
        history.push(`/intranet/holidays/${id}/edit`);
    };

    const handleCreate = () => {
        history.push('/intranet/holidays/create');
    };

    const handleDelete = (holiday: Holiday) => {
        requestConfirmation({
            title: '¿Eliminar feriado?',
            message: `¿Estás seguro de eliminar el feriado "${holiday.name}"? Esta acción no se puede deshacer.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteHoliday(holiday.id, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || 'Feriado eliminado correctamente');
                                loadHolidays();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || 'Error al eliminar el feriado');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const handleToggleActive = (holiday: Holiday) => {
        const newStatus = !holiday.is_active;
        const actionText = newStatus ? 'activar' : 'desactivar';
        
        requestConfirmation({
            title: `¿${newStatus ? 'Activar' : 'Desactivar'} feriado?`,
            message: `¿Estás seguro de ${actionText} el feriado "${holiday.name}"?`,
            buttons: {
                confirmButton: {
                    label: `Sí, ${actionText}`,
                    onClick: () => {
                        activeHoliday(holiday.id, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || `Feriado ${newStatus ? 'activado' : 'desactivado'} correctamente`);
                                loadHolidays();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || `Error al ${actionText} el feriado`);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const getRecurrenceBadgeColor = (type: string): string => {
        switch (type) {
            case 'unique':
                return 'bg-info';
            case 'weekly':
                return 'bg-warning';
            case 'monthly':
                return 'bg-success';
            case 'annual':
                return 'bg-primary';
            default:
                return 'bg-secondary';
        }
    };
    
    // Mostrar loading mientras se cargan los datos
    if (fetchingGetHolidays) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Feriados', isCurrent: true }
                ]}
                title="Gestión de Feriados"
                description="Administra los días feriados del sistema, incluyendo feriados únicos y recurrentes"
            />
            
            <DefaultCard>
                {/* Filtros y Búsqueda */}
                <div className="row mb-4 align-items-end">
                    <div className="col-md-4">
                        <InputField
                            label="Buscar"
                            value={searchTerm}
                            onChange={(value) => setSearchTerm(value)}
                            placeholder="Buscar por nombre o descripción..."
                            icon="fa-search"
                        />
                    </div>
                    <div className="col-md-3">
                        <SelectTwo
                            label="Tipo de Recurrencia"
                            inputValue={recurrenceFilter}
                            onChange={(option: any) => setRecurrenceFilter(option?.value || '')}
                            options={recurrenceTypeOptions}
                            isClearable
                            placeholder="Todos los tipos"
                        />
                    </div>
                    <div className="col-md-5 d-flex justify-content-end">
                        <ButtonGeneral
                            onClick={handleCreate}
                            title="Nuevo Feriado"
                            variant="primary"
                            size="small"
                            icon="fa-plus"
                            iconPosition="left"
                        />
                    </div>
                </div>
                
                {/* Tabla */}
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Fecha</th>
                                <th>Tipo</th>
                                <th>Recurrencia</th>
                                <th>Estado</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHolidays.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-4">
                                        <i className="fas fa-calendar-times fa-3x mb-3 d-block text-secondary"></i>
                                        {searchTerm || recurrenceFilter
                                            ? 'No se encontraron feriados con los filtros aplicados'
                                            : 'No hay feriados registrados. ¡Crea el primero!'}
                                    </td>
                                </tr>
                            ) : (
                                filteredHolidays.map((holiday) => (
                                    <tr 
                                        key={holiday.id}
                                        style={{ opacity: holiday.is_active ? 1 : 0.6 }}
                                    >
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                    style={{ 
                                                        width: '36px', 
                                                        height: '36px', 
                                                        backgroundColor: 'rgba(74, 144, 226, 0.1)' 
                                                    }}
                                                >
                                                    <i className="fas fa-calendar-day" style={{ color: '#4A90E2' }}></i>
                                                </div>
                                                <div>
                                                    <span className="fw-bold d-block" style={{ color: '#001849' }}>
                                                        {holiday.name}
                                                    </span>
                                                    {holiday.description && (
                                                        <small className="text-muted">
                                                            {holiday.description.length > 40
                                                                ? `${holiday.description.substring(0, 40)}...`
                                                                : holiday.description}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="fw-semibold">
                                                {holiday.date_formatted}
                                            </span>
                                        </td>
                                        <td>
                                            <span 
                                                className={`badge ${getRecurrenceBadgeColor(holiday.recurrence_type)}`}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                {holiday.recurrence_type_display}
                                            </span>
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {holiday.recurrence_description}
                                            </small>
                                        </td>
                                        <td>
                                            <span 
                                                className={`badge ${holiday.is_active ? 'bg-success' : 'bg-secondary'}`}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                {holiday.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <ButtonTableAction
                                                    callbackFunction={() => handleEdit(holiday.id)}
                                                    title="Editar"
                                                    classIcon="fa-edit"
                                                    colorIcon="text-warning"
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleToggleActive(holiday)}
                                                    title={holiday.is_active ? 'Desactivar' : 'Activar'}
                                                    classIcon={holiday.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}
                                                    colorIcon={holiday.is_active ? 'text-success' : 'text-secondary'}
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleDelete(holiday)}
                                                    title="Eliminar"
                                                    classIcon="fa-trash"
                                                    colorIcon="text-danger"
                                                    sizeIcon="16"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Información */}
                <div className="mt-3 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        Mostrando {filteredHolidays.length} de {holidays.length} feriado(s)
                    </small>
                    <div className="d-flex gap-2">
                        {recurrenceTypeOptions.slice(1).map((type) => (
                            <small 
                                key={type.value}
                                className="d-flex align-items-center gap-1"
                            >
                                <span 
                                    className={`badge ${getRecurrenceBadgeColor(type.value)}`}
                                    style={{ fontSize: '0.65rem', padding: '0.25em 0.4em' }}
                                >
                                    •
                                </span>
                                <span className="text-muted">{type.label}</span>
                            </small>
                        ))}
                    </div>
                </div>
            </DefaultCard>
        </div>
    );
};

export default HolidayList;

